<template>
  <v-row class="app-preview">
    <v-col
      cols="12"
      md="9"
    >
      <v-card class="app-merchant-editable">
        <!-- Header -->
        <div class="py-5 px-5">
          <div class="d-flex flex-wrap justify-space-between">
            <!-- Date -->
            <div class="d-flex align-center">
              <span class="me-2 font-weight-medium text--primary">Create Niagamas Merchant</span>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Customer Details -->
        <div class="py-5 px-5">
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Registration Date -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Registration Date:
                  </p>
                  <v-menu
                    v-model="isDateMenuOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="new Date(merchant.registration_date).toLocaleDateString()"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="merchant.registration_date"
                      color="primary"
                      :first-day-of-week="1"
                      @input="isDateMenuOpen = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <!-- Name -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                  class="offset-sm-0"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Merchant Name:
                  </p>
                  <v-text-field
                    v-model="merchant.name"
                    single-line
                    dense
                    outlined
                    hide-details
                    placeholder="Merchant Name"
                  ></v-text-field>
                </v-col>

                <!-- Contact Number -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Contact Person No.:
                  </p>
                  <v-text-field
                    v-model="merchant.contact_person_number"
                    single-line
                    dense
                    outlined
                    type="number"
                    :rules="[v => /[0-9\-().\s]{7}$/.test(v) || 'Must be valid contact number']"
                    hide-details="auto"
                    placeholder="Contact Number"
                    prefix="+673"
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- ROC Number -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    ROC No.:
                  </p>
                  <v-text-field
                    v-model="merchant.roc_number"
                    single-line
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="ROC Number"
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    16/17 Attachment
                  </p>
                  <v-file-input
                    prepend-icon=""
                    :append-icon="icons.mdiPaperclip"
                    accept="application/pdf, image/png, image/jpg, image/jpeg"
                    show-size
                    dense
                    outlined
                    :rules="certUploadRules"
                    label="Upload 16/17"
                    hide-details="auto"
                    @change="directUploadCert($event)"
                  >
                    <template v-slot:selection="{ text }">
                      <v-chip
                        small
                        label
                        color="primary"
                      >
                        {{ text }}
                      </v-chip>
                    </template>

                    <template v-slot:append>
                      <v-progress-circular
                        v-if="uploadLoading"
                        color="primary"
                        indeterminate
                        size="25"
                      ></v-progress-circular>
                    </template>
                  </v-file-input>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      md="3"
    >
      <v-card class="mb-6">
        <v-card-text>
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="isCommentDialogVisible = true"
          >
            {{ t('Send Request') }}
          </v-btn>

          <div class="text-center mt-4">
            {{ t('Please ensure that all fields are filled correctly as this record will not be editable after confirming.') }}
          </div>
        </v-card-text>
      </v-card>
      <v-card
        v-if="errors.length != 0"
        class="mb-6"
      >
        <v-card-text>
          <Alert
            :messages="errors"
            color="error"
          ></Alert>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Comment dialog -->
    <v-dialog
      v-model="isCommentDialogVisible"
      width="500"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Request to create merchant?
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isCommentDialogVisible = false"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-textarea
            v-model="newComment"
            dense
            outlined
            hide-details="auto"
            placeholder="Leave your comment here (optional)"
            :disabled="loading"
            class="mb-4"
          ></v-textarea>
          <p>
            This action will create an create request & the merchant will be saved after approved by auditor.
          </p>
          <p class="mb-0">
            Please leave any remarks that the auditor should know about the changes.
          </p>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="saveRequest()"
          >
            Yes, send request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mdiCalendar, mdiPaperclip } from '@mdi/js'
import {
  inject,
  onMounted,
  ref,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import Alert from '@/components/Alert'

export default {
  components: {
    Alert,
  },
  setup() {
    // Initializers
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const { router } = useRouter()

    // Properties
    const merchant = ref({
      registration_date: new Date().toISOString().substring(0, 10),
      name: null,
      contact_person_number: null,
      roc_number: null,
      merchant_cert: null,
    })
    const errors = ref([])
    const userData = JSON.parse(localStorage.getItem('userData'))
    const dateToday = new Date().toLocaleDateString('en-SG')
    const loading = ref(false)
    const isDateMenuOpen = ref(false)
    const isCommentDialogVisible = ref(false)
    const newComment = ref(null)
    const uploadLoading = ref(false)
    const certUploadRules = [value => !value || value.size < 5000000 || 'Certificate file has to be lower than 5MB']

    // Methods
    const saveRequest = () => {
      delete merchant.value.merchant_cert_url
      delete merchant.value.merchant_cert_name
      loading.value = true
      errors.value = []
      store
        .dispatch('customerStore/createNiagamasMerchant', merchant.value)
        .then(response => {
          const { data } = response.data
          merchant.value.id = data.id
          const requestParam = {
            record_id: merchant.value.id,
            record_type: 'NiagamasMerchant',
            requested_by_id: userData.id,
            action: 'Create',
            params: merchant.value,
            rejected_at: null,
            responded_by: null,
            comment: newComment.value,
          }
          store
            .dispatch('requestStore/createRequest', requestParam)
            .then(res => {
              snackbarService.success(res.data.message)
              router.push('/apps/requests/list')
            })
            .catch(error => {
              loading.value = false
              isCommentDialogVisible.value = false
              errors.value = error.response.data.errors
              snackbarService.error(error.response.data.message || 'Something went wrong while saving request. Please refresh!')
            })
        })
        .catch(error => {
          loading.value = false
          isCommentDialogVisible.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while saving merchant. Please refresh!')
        })
    }
    const isNumber = event => {
      const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
      const keyPressed = event.key
      if (!keysAllowed.includes(keyPressed)) {
        event.preventDefault()
      }
    }
    const directUploadCert = file => {
      if (file == null) {
        merchant.value.merchant_cert = null
        merchant.value.merchant_cert_name = null

        return
      }

      uploadLoading.value = true
      store
        .dispatch('customerStore/directUploadMerchantCert', file)
        .then(response => {
          uploadLoading.value = false
          if (response) {
            merchant.value.merchant_cert = response.signed_id
            merchant.value.merchant_cert_name = response.filename
          }
        })
        .catch(error => {
          uploadLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while uploading 16/17. Please refresh!')
        })
    }

    // Mounted
    onMounted(() => {
    })

    return {
      t,
      merchant,
      errors,
      saveRequest,
      userData,
      dateToday,
      loading,
      isNumber,
      directUploadCert,
      uploadLoading,
      certUploadRules,
      isDateMenuOpen,
      isCommentDialogVisible,
      newComment,

      icons: {
        mdiCalendar,
        mdiPaperclip,
      },
    }
  },
}
</script>
